/**
 * Fetches USD high-impact economic calendar events from the server API
 * (JBlanked / Forex Factory). No local CSV — API only.
 */

import { API_ENDPOINTS } from '../config';

function normalizeDateKey(d) {
  if (!d || typeof d !== 'string') return '';
  const s = d.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

function rowsToMap(rows) {
  const map = new Map();
  for (const row of rows) {
    const date = normalizeDateKey(row.date);
    if (!date) continue;
    const list = map.get(date) || [];
    const { date: _drop, ...rest } = row;
    list.push({
      ...rest,
      time: row.time || '',
      currency: row.currency || 'USD',
      event: row.event || '',
    });
    map.set(date, list);
  }
  return map;
}

const CALENDAR_ROW_CACHE_VER = 12;

const cache = new Map();

function normalizeFilterValue(impacts) {
  const list = Array.isArray(impacts)
    ? impacts
    : String(impacts || '')
        .split(',')
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean);
  if (list.length === 1 && list[0] === 'ff-red') return ['ff-red'];
  if (!list.length) return ['ff-red'];
  return list;
}

/**
 * @param {string} [monthKey] YYYY-MM (defaults to current month)
 * @param {string[]|string} [impacts] impact labels (high, medium, low, none)
 * @returns {Promise<Map<string, Array<Record<string, unknown>>>>}
 */
export function loadUsHighImpactEvents(monthKey, impacts) {
  if (!monthKey) {
    const now = new Date();
    monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  const impactList = normalizeFilterValue(impacts);
  const impactKey = impactList.join(',');

  const cacheKey = `${monthKey}:${impactKey}:${CALENDAR_ROW_CACHE_VER}`;
  const cached = cache.get(cacheKey);
  if (cached?.map) return Promise.resolve(cached.map);
  if (cached?.promise) return cached.promise;

  const promise = fetch(API_ENDPOINTS.economicCalendarHighImpact(monthKey, impactList))
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((rows) => {
      const map = rowsToMap(Array.isArray(rows) ? rows : []);
      cache.set(cacheKey, { map, promise: null });
      return map;
    })
    .catch(() => {
      cache.delete(cacheKey);
      return new Map();
    });

  cache.set(cacheKey, { map: null, promise });
  return promise;
}

/**
 * @param {string} mondayDateStr sheet format mm.dd.yyyy (Monday)
 * @param {Map<string, unknown[]>} eventsByDate
 */
export function highImpactTouchesWeekMonday(mondayDateStr, eventsByDate) {
  if (!eventsByDate?.size) return false;
  const parts = (mondayDateStr || '').split('.');
  if (parts.length !== 3) return false;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return false;
  for (let i = 0; i < 5; i++) {
    const d = new Date(yyyy, mm - 1, dd + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (eventsByDate.has(key)) return true;
  }
  return false;
}

/**
 * @param {number} year
 * @param {number} monthIndex0
 * @param {Map<string, unknown[]>} eventsByDate
 */
export function monthHasHighImpactEvent(year, monthIndex0, eventsByDate) {
  if (!eventsByDate?.size) return false;
  const prefix = `${year}-${String(monthIndex0 + 1).padStart(2, '0')}-`;
  for (const k of eventsByDate.keys()) {
    if (k.startsWith(prefix)) return true;
  }
  return false;
}
